import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, exhaustMap, map, share, tap } from 'rxjs/operators';
import { Note, NoteProjectionType, NoteRenderUpdateResult, NoteResponse } from './note.model';
import { Observable, of, timer } from 'rxjs';
import { HALResponse } from '../../shared/model/app.model';
import { LogService } from '../../logs/log.service';
import { TagService } from '../../tags/tag.service';
import { GeneralParameters, NoteProjections } from "app/shared/model/request-parameter.model";
import { PagedEntity } from "app/shared/model/app.model";
import { NoteMetadata } from './note-metadata.model';
import { HasId } from "app/shared/model/properties.model";
import { Utils } from "ng/src/app/core/utils";
import { ServiceEntityNotifier } from "ng/src/app/shared/service-entity-notifier";

@Injectable({
	providedIn: 'root'
})
export class NoteService extends ServiceEntityNotifier<Note> {
	constructor(
		private http: HttpClient,
		private tagService: TagService,
	) {
		super();
	}
	
	private draftPoll$?: Observable<Note.Projections.KeyNoteTemplateValues[] | HttpErrorResponse | Error>
	
	getRecentNotes(logId?: number) {
		return this.getRecentNotesForLogs(logId ? [logId] : undefined);
	}
	
	getRecentNotesForLogs(logIds?: number[], headers?: {[headerName: string]: string}) {
		let params = new HttpParams().set('isMostRecent', 'true')
			.set('size','10000');
		let httpHeaders = new HttpHeaders();
		logIds && logIds.forEach(id => params = params.append('logId', String(id)));
		headers && Object.keys(headers).forEach(header => httpHeaders = httpHeaders.set(header, headers[header]));

		return this.http.get<HALResponse<{ notes: Note[] }>>(
			'/api/notes', { params: params, headers: httpHeaders }
		)
		.pipe(
			map(r => r._embedded.notes)
		);
	}

	getNoteTimeBoundary(logId: number, time: 'earliest' | 'latest', filterByReportId?: number | null | undefined) {
		const earliest = time == 'earliest',
		params = {
			log: logId,
			size: 1,
			...earliest && { sort: ['timeframe,asc'] },
			...!earliest && { isMostRecent: true },
			...filterByReportId && { report: filterByReportId }
		} as Note.Query;

		return this.getLogNotes(params).pipe(
			map(note => note[0]?.[earliest ? 'timeframe' : 'updatedOrTimeframeEnded'])
		);
	}

	getLogNotes<T extends Note>(params: Note.Query): Observable<NoteResponse<T>> {
		return this.http.get<HALResponse<{ notes: T[] }>>('/api/notes', { params: params })
			.pipe(
				map(r => {
					const notes = PagedEntity.from(r._embedded.notes, r.page);
					
					notes.forEach(this.sortComments);

					return notes;
				})
			);
	}
	
	getLogNote<Type extends NoteProjections>(id: number, projection?: Type): Observable<NoteProjectionType<Type>> {
		const params: Note.Query = {};
		if (projection)
			params.projection = projection;
		
		return this.http.get<NoteProjectionType<Type>>(`/api/notes/${id}`, { params: params }).pipe(
			tap(note => {
				if (Array.isArray(note?.noteComments))
					this.sortComments(note as Pick<Note, 'noteComments'>)
			})
		)
	}
	
	getNoteMetadata(params: NoteMetadata.QueryParams): Observable<PagedEntity<NoteMetadata>> {
		return this.http.get<HALResponse<{ noteMetadata: NoteMetadata[] }>>('/api/noteMetadata', {
			params: params as GeneralParameters
		})
			.pipe(
				map((res) => PagedEntity.from(res._embedded.noteMetadata, res.page))
			)
	}

	getDraftNotesForUser(options?: Partial<{ size: number, template: number | number[]}>): Observable<NoteResponse<Note.Projections.KeyNoteTemplateValues>> {
		const params: Note.Query = {
			projection: NoteProjections.keyTemplateValues,
			draft: true,
			sort: 'created',
			'coCreators.user': LogService.selectedLog?.activeLogUser?.user?.id
		};
		if (options?.size) {
			params.size = options.size;
		}
		if (options?.template) {
			params.template = options.template;
		}
		return this.getLogNotes(params) as Observable<NoteResponse<Note.Projections.KeyNoteTemplateValues>>
	}
	
	getPastValueNotes<T extends NoteProjections>(referenceNote: HasId | number, projection: T, query: Note.Query = { size: 20, sort: 'noteTime,desc' }) {
		if (typeof referenceNote !== 'number') {
			referenceNote = referenceNote.id;
		}
		const params: Note.Query = {
			...query,
			excludeIds: referenceNote,
			sameKeyFieldsAsNoteId: referenceNote,
			beforeNoteIdByNoteTime: referenceNote,
			projection: projection
		}
		return this.getLogNotes(params) as Observable<PagedEntity<NoteProjectionType<T>>>;
	}

	patchNote(note: number | Note, toSend: Partial<Omit<Note.Model , 'id'>>) {
		const noteId = typeof note === 'number' ? note : note.id;

		return this.http.patch(`/api/notes/${noteId}`, toSend);
	}
	
	deleteNote(note: Note) {
		return this.http.delete(note._links.self.href);
	}

	createNewNotesPoll(noteParams: (() => Note.Query) | Note.Query, period = 10000): Observable<Note[] | HttpErrorResponse | Error> {
		return timer(0, period).pipe(
			exhaustMap(() =>
				this.getLogNotes(typeof noteParams == 'function' ? noteParams() : noteParams).pipe(
					catchError(err => of(err))
				)
			)
		);
	}
	
	getDraftNotesPoll() {
		if (this.draftPoll$)
			return this.draftPoll$;
		
		return this.draftPoll$ = (this.createNewNotesPoll(() => ({
			projection: NoteProjections.keyTemplateValues,
			draft: true,
			sort: 'created',
			'coCreators.user': LogService.selectedLog?.activeLogUser?.user?.id,
		})) as Observable<Note.Projections.KeyNoteTemplateValues[] | HttpErrorResponse | Error>)
		.pipe(
			share()
		);
	}

	getNoteStyleClasses(note: Pick<Note, 'newMillis' | 'tagValues' | 'richText'>, useNewNoteStyles = true) {
		let styles: string[] = [];

		// New Note styles take precedence over tag styles
		if (useNewNoteStyles && note.newMillis && LogService.selectedLog?.newNoteStyles.length)
			styles = LogService.selectedLog.newNoteStyles.map(s => s.description);
		else
			styles = this.tagService.getStylesForTagValues(note.tagValues);
		
		if (note.richText)
			styles.push('ql-editor');
		
		return styles;
	}
	
	updateNoteRender(idType: 'template' | 'log', id: number, dates?: {before?: Date, after?: Date}) {
		const body: Record<string, any> = {
			[`${idType}Id`]: id
		}
		if (dates?.before) {
			body.before = dates?.before.toISOString();
		}
		if (dates?.after) {
			body.after = dates?.after.toISOString();
		}
		
		return this.http.post<NoteRenderUpdateResult>('/api/noteUtility/updateRender', body);
	}
	
	private sortComments(note: Pick<Note, 'noteComments'>) {
		if (!Array.isArray(note?.noteComments) || isNaN( new Date(note.noteComments[0]?.created).getTime() )) return;
		
		note.noteComments.sort(Utils.createSortFunction(comment => new Date(comment.created), 'desc'));
	}
	
	protected readonly getItems = this.getLogNotes.bind(this);
}